import useCompletedChatMessage from 'hooks/chat/useCompletedChatMessage';
import useChatSummariesReconciliation from 'hooks/chat/useChatSummariesReconciliation';
import { useCallback, useEffect, useState } from 'react';
import chatService from 'services/chat';

const useAssignmentChatSummary = ({ assignmentId, courseId, ownerAccountId, reconciliationEnabled = true }) => {
    const [assignmentChat, setAssignmentChat] = useState(null);

    const refreshAssignmentChatSummary = useCallback(async () => {
        if (!assignmentId || !ownerAccountId || !courseId) return null;

        const nextChat = await chatService.getAssignmentChatSummary({
            active: true,
            owner_account_id: ownerAccountId,
            course_id: courseId,
            assignment_id: assignmentId,
        });

        setAssignmentChat(nextChat);
        return nextChat;
    }, [assignmentId, courseId, ownerAccountId]);

    useEffect(() => {
        if (!assignmentId || !ownerAccountId || !courseId) {
            setAssignmentChat(null);
            return;
        }

        let isMounted = true;

        const loadAssignmentChat = async () => {
            try {
                const nextChat = await chatService.getAssignmentChatSummary({
                    active: true,
                    owner_account_id: ownerAccountId,
                    course_id: courseId,
                    assignment_id: assignmentId,
                });

                if (!isMounted) return;
                setAssignmentChat(nextChat);
            } catch (error) {
                if (!isMounted) return;
                console.error(error);
            }
        };

        loadAssignmentChat();

        return () => {
            isMounted = false;
        };
    }, [assignmentId, courseId, ownerAccountId]);

    useCompletedChatMessage(async ({ assignment_id: completedAssignmentId }) => {
        if (completedAssignmentId !== assignmentId) return;
        await refreshAssignmentChatSummary();
    });

    useChatSummariesReconciliation({
        chats: assignmentChat ? [assignmentChat] : [],
        enabled: Boolean(assignmentChat?.id) && reconciliationEnabled,
        onOutOfSync: async (chatId) => {
            if (chatId !== assignmentChat?.id) return;
            await refreshAssignmentChatSummary();
        },
    });

    return {
        assignmentChat,
        refreshAssignmentChatSummary,
        setAssignmentChat,
    };
};

export default useAssignmentChatSummary;
